import {authService} from '@/authService';

let configCache;

export default (client) => ({
  addResponseErrorHandler(handleResponseError) {
    const interceptorId = client.interceptors.response.use(undefined, async (error) => {
      await handleResponseError(error);
      throw error;
    });

    return () => client.interceptors.response.eject(interceptorId);
  },
  async execute(method, resource, data) {
    // inject the accessToken for each request
    const accessToken = await authService.getAccessToken();
    return client({
      method,
      url: resource,
      data,
      headers: {
        ...(accessToken && {Authorization: `Bearer ${accessToken}`}),
      },
    }).then((req) => {
      return req.data;
    });
  },
  async getConfig() {
    if (!configCache) {
      const {data} = await client({
        method: 'get',
        url: '/v1/appconfig',
      });
      configCache = data;
    }
    return configCache;
  },
  isUnauthenticatedError(error) {
    return error.response && error.response.status === 401;
  },
  isUnauthorisedError(error) {
    return error.response && error.response.status === 403;
  },
  // wishful thinking that we'll get to put this config in the API
  isFederalDeployment(host = window.location.host) {
    return host.includes('federal.');
  },
  async canProvisionSandbox() {
    const {enableSandboxProvisioning} = await this.getConfig();
    return enableSandboxProvisioning;
  },
  createGithubRepository(projectName, repoName) {
    return this.execute('put', `/v1/github/repository`, {projectName, repoName});
  },
  createProject(data) {
    return this.execute('post', '/v1/projects', data);
  },
  getFinanceProducts() {
    return this.execute('get', `/v1/financeproducts`);
  },
  getAwsProject(projectName) {
    return this.execute('get', `/v1/awsProjects/${projectName}`);
  },
  getAwsProjects() {
    return this.execute('get', `/v1/awsProjects`);
  },
  getProject(projectName) {
    return this.execute('get', `/v1/project/${projectName}`);
  },
  getProjects() {
    return this.execute('get', '/v1/projects');
  },
  getMetrics(sheetRange, dataRange) {
    return this.execute('get', '/metrics?sheetRange=' + sheetRange + '&dataRange=' + dataRange);
  },
  getResourceIndicator(projectName) {
    return this.execute('get', `/v1/resources/${projectName}`);
  },
  getGithubProjectImports(projectName) {
    return this.execute('get', `/v1/github/project/${projectName}/imports`);
  },
  getGithubProjectLinks(projectName) {
    return this.execute('get', `/v1/github/project/${projectName}/links`);
  },
  getJiraTickets() {
    // console.log('get jira api called');
    return this.execute('get', `/v1/jiratickets`);
  },
  getGithubProjectReadiness(projectName) {
    return this.execute('get', `/v1/github/project/${projectName}/readiness`);
  },
  githubImport(projectName) {
    return this.execute('put', `/v1/github/import`, {projectName});
  },
  refreshGithubSecretsForAzureEnvs(projectName) {
    return this.execute('put', `/v1/github/refreshSecretsForAzureEnvs`, {projectName});
  },
  refreshGithubSecretsForAWSEnvs(projectName) {
    return this.execute('put', `/v1/github/refreshSecretsForAWSEnvs`, {projectName});
  },
  archiveGithubRepository(repoName) {
    return this.execute('delete', `/v1/github/repository/${repoName}`);
  },
  updateGithubRepository(repoName, updates) {
    return this.execute('patch', `/v1/github/repository/${repoName}`, updates);
  },
  updateProject(projectName, projectData) {
    return this.execute('put', '/v1/projects/' + projectName, projectData);
  },
  approveProject(project) {
    return this.execute('put', `/v1/approvals/projects/${project.projectName}`, project);
  },
  rejectProject(project) {
    return this.execute('put', `/v1/rejections/projects/${project.projectName}`, project);
  },
  deleteProject(project) {
    return this.execute('delete', '/v1/projects/' + project);
  },
  provisionProject(project) {
    return this.execute('post', `/v1/provision/${project.projectName}`, project);
  },
  provisionSandbox(project) {
    return this.execute('post', `/v1/sandbox/${project.projectName}`, project);
  },
  deleteProvisionedProject(project, deleteAll, sandboxstatus) {
    // console.log(deleteAll, sandboxstatus);
    if (deleteAll || project.environments.length == 0) {
      if (deleteAll && sandboxstatus) {
        return this.execute('delete', `/v1/deletion/${project.projectName}/${sandboxstatus}/true`, project);
      } else if (sandboxstatus) {
        return this.execute('delete', `/v1/deletion/${project.projectName}/${sandboxstatus}/false`, project);
      } else {
        return this.execute('delete', `/v1/deletion/${project.projectName}/${sandboxstatus}/true`, project);
      }
    }
    return this.execute('delete', `/v1/provision/${project.projectName}/${project.environments}/${sandboxstatus}`, project);
  },
  unstickProject(project) {
    return this.execute('post', `/v1/provision/${project.projectName}/unstick`);
  },
  getProjectLogs(projectName) {
    return this.execute('get', `v1/provision/history/${projectName}`);
  },
  getProjectLogDetails(projectName, provisionid) {
    return this.execute('get', `v1/provision/history/${projectName}/${provisionid}`);
  },
  getEnvironments() {
    return this.execute('get', '/v1/environments');
  },
  getRepositories(projectName) {
    return this.execute('get', `v1/project/${projectName}/repositories`);
  },
  isApiAvailable() {
    // by-pass API check for local development
    if (window.location.host.indexOf('localhost') > -1) return true;
    return this.execute('get', '/liveness');
  },
  validateCoreIds(coreIds) {
    return this.execute('post', '/v1/validate/coreids', coreIds);
  },
  getNameFromCoreId(coreIds) {
    return this.execute('post', '/v1/validate/getnamesfromids', coreIds);
  },
  getEmbedTokenInGroup(data) {
    return this.execute('post', '/v1/power-bi/embed-token-in-group', data);
  },
  createBreakTheGlassRequest(data) {
    return this.execute('post', '/v1/break-the-glass/access', data);
  },
  getBreakTheGlassEnvironments() {
    return this.execute('get', '/v1/break-the-glass/environments');
  },
  getApps(projectName) {
    return this.execute('get', `/v1/credman/${projectName}/get-apps`);
  },
  deleteSecretApi(data) {
    return this.execute('delete', 'v1/credman/delete-secret', data);
  },
  createApp(data) {
    return this.execute('post', '/v1/credman/create-app', data);
  },
  deleteApp(data) {
    return this.execute('delete', 'v1/credman/delete-app', data);
  },
  createSecret(data) {
    return this.execute('post', 'v1/credman/create-secret', data);
  },
  getSecretDetails(data) {
    return this.execute('post', 'v1/credman/get-secrets', data);
  },
  deleteCredman(data, deleteAll) {
    if (deleteAll || data.credmanEnvironments.length == 0) {
      return this.execute('delete', `/v1/credman/delete-project`, data);
    }
    return this.execute('delete', `/v1/credman/delete-environments`, data);
  },
  createOrUpdateScheduleDeletion(data) {
    return this.execute('post', '/v1/projects/scheduledelete', data);
  },
  getScheduleDeletion(data) {
    return this.execute('get', `/v1/projects/getscheduledelete/${data.projectName}`);
  },
  createCustomResourceGroup(data) {
    return this.execute('post', '/v1/provision/multiresource', data);
  },
  getCustomResourceGroups(projectName) {
    return this.execute('get', `/v1/provision/multiresource/${projectName}`);
  },
  deleteCustomResourceGroup(data) {
    return this.execute('delete', '/v1/provision/multiresource', data);
  },
  createCustomK8Namespace(data) {
    return this.execute('post', '/v1/provision/customnamespace', data);
  },
  getCustomK8Namespace(projectName) {
    return this.execute('get', `/v1/provision/customnamespace/${projectName}`);
  },
  deleteCustomK8Namespace(data) {
    return this.execute('delete', '/v1/provision/customnamespace', data);
  },
});
